import { useMemo, useState } from 'react';
import { Scenario } from '../types/model';
import { commonOutputScenarios } from '../utils/modelComparison';
import { modelLabel, datasetLabel } from '../utils/modelTargets';
import ScenarioVisualization from './ScenarioVisualization';

export default function ModelComparison({ scenarios }: { scenarios: Scenario[] }) {
  const [tab, setTab] = useState<'common' | 'specific'>('common');
  const common = useMemo(() => {
    try { return { runs: commonOutputScenarios(scenarios), error: '' }; }
    catch (error) { return { runs: [], error: (error as Error).message }; }
  }, [scenarios]);
  const groups = Array.from(new Set(scenarios.map(s => `${s.framework}/${s.dataset}`))).map(key => ({
    key, runs: scenarios.filter(s => `${s.framework}/${s.dataset}` === key),
  }));

  return <section aria-label="Cross-model scenario comparison" className="model-comparison">
    
    <div role="tablist" aria-label="Comparison views" className="comparison-tabs">
      <button role="tab" id="comparison-common-tab" aria-controls="comparison-common" aria-selected={tab === 'common'} onClick={() => setTab('common')}>Common outcomes</button>
      <button role="tab" id="comparison-specific-tab" aria-controls="comparison-specific" aria-selected={tab === 'specific'} onClick={() => setTab('specific')}>Model-specific outputs</button>
    </div>
    <div role="tabpanel" id="comparison-common" aria-labelledby="comparison-common-tab" hidden={tab !== 'common'}>
      {tab === 'common' && <>
        <p className="comparison-note">C and Pᶜ refer to combined final demand in CP2015 and household consumption in Furusawa2023; w is primary-factor income in CP2015 and labor income in Furusawa2023. Nominal outputs retain each model’s numeraire.</p>
        {common.error ? <p role="alert">{common.error}</p>
          : common.runs.length ? <ScenarioVisualization scenarios={common.runs} initialVariable="counterfactual.pi" />
          : <p>Select completed runs from both models.</p>}
      </>}
    </div>
    <div role="tabpanel" id="comparison-specific" aria-labelledby="comparison-specific-tab" hidden={tab !== 'specific'}>
      {tab === 'specific' && <>
        <p className="comparison-note">These panels retain each model’s definitions and units; similarly named variables need not measure the same object.</p>
        <div className="model-output-grid">
          {groups.map(({ key, runs }) => <section key={key} className="model-output-panel" aria-label={`${modelLabel(runs[0].framework)} outputs`}>
            <h4>{modelLabel(runs[0].framework)}</h4>
            <p className="baseline-dataset">{datasetLabel(runs[0].dataset)}</p>
            <p className="comparison-note">{runs[0].framework === 'CP2015'
              ? 'C: combined real final demand. w: primary-factor price. Pᶜ: final-demand basket price.'
              : 'C: real household consumption. w: labor income. Pᶜ: household consumption basket price.'}</p>
            <ScenarioVisualization initialVariable="hats.C" scenarios={runs} framework={runs[0].framework} dataset={runs[0].dataset} />
          </section>)}
        </div>
      </>}
    </div>
  </section>;
}
